import type { UserThemeDensity } from "@neucleos/theme";

import { CONTEXT_RAIL_PX, DECK_MIN_PX, SIDE_RIB_PX } from "../neucleos/agentRailFold";
import { DESK_DOCK_TIGHT_PX } from "./dockPreferences";

export const CONTEXT_RAIL_WIDTH_STORAGE_KEY = "cleo:context-rail-width";
/**
 * The donor furniture width (CLEO-CAN FD02: one source for the numbers) —
 * the fold math keeps reading the donor's formula with the LIVE rail width,
 * so this constant names the cockpit's fixed track, not Cleo Code's rest.
 */
export const CONTEXT_RAIL_FURNITURE_WIDTH = CONTEXT_RAIL_PX;
/**
 * DATED REVISION 2026-09-02 (RL-CAN-39; the principal: "Just do whatever you
 * think is optimal" after agreeing the ceiling): the rail RESTS at the design
 * of record's 396 — the width where the inline filter, the delta line and the
 * two-up Media face are present at first paint. The donor's 280 was the
 * cockpit's fixed track; a person-resizable rail rests where its own laws read
 * well. WS7's allocator still bounds every hand act with the live width.
 */
export const CONTEXT_RAIL_DEFAULT_WIDTH = 396;
// The pre-resizable rail's fixed `w-52` width; narrower stops being readable.
export const CONTEXT_RAIL_MIN_WIDTH = 208;
/**
 * DATED REVISION 2026-09-02 (RL-CAN-39; the principal's "agreed" on the 560
 * ceiling): 720, so a wide dock still leaves a 560 card and Media can go
 * four-up. Reachable only when the viewport reserve below affords it.
 */
export const CONTEXT_RAIL_MAX_WIDTH = 720;
/** The manual and automatic folded stop; one width preference, no fold flag. */
export const CONTEXT_RAIL_RIB_WIDTH = SIDE_RIB_PX;
// DATED REVISION 2026-09-01 (FD-CAN-17, the principal's mirror): tab
// presentation follows the context DOCK's per-viewer density latch now —
// icon when tight, labeled when wide — mirroring the left dock's law. The
// rail-width rule (labeled at >= 400px measured rail) retired with the
// horizontal strip it governed; width remains the rail's own resize law.
export type ContextRailTabPresentation = "icon" | "labeled";

/** The dock row's 14px insets on both sides (FD-CAN-33). */
export const DESK_FRAME_INSETS_PX = 2 * 14;

/**
 * Widest the rail may grow at a given viewport: reserve the deck's minimum
 * beside the tightest left furniture, cap at the hard maximum, and never fall
 * below the rail's own minimum (at small viewports the rail simply stays at
 * its floor; the fold ladder decides what actually renders).
 *
 * DATED REVISION 2026-10-07 (Widen Cleo: one desk minimum): the reserve is the
 * allocator's DECK_MIN_PX, the deck minimum every fold already reads, plus the
 * dock row's insets and the dock's tight column. It used to reserve the
 * Settings screen's 640px content minimum and 208px sidebar, which the desk
 * never renders, so the ceiling and the fold disagreed about the deck.
 */
export function resolveContextRailMaximumWidth(viewportWidth: number): number {
  const available =
    Math.floor(viewportWidth) - DESK_FRAME_INSETS_PX - DESK_DOCK_TIGHT_PX - DECK_MIN_PX;
  return Math.max(CONTEXT_RAIL_MIN_WIDTH, Math.min(CONTEXT_RAIL_MAX_WIDTH, available));
}

/*
 * RL-CAN-39 — the card measure. The rail's content lives in the ringed card
 * (FD-CAN-28: 14px of shell ground to its left) beside the dock column
 * (FD-CAN-17: 48px tight, 160px wide). Every width breakpoint inside the
 * card reads THIS width, so a wide dock at 396 opens the compact filter door
 * instead of crowding the inline row. Computed from the rail's one width
 * preference and the dock's one density latch; stored nowhere (DL4).
 */
export const CONTEXT_CARD_INSET_PX = 14;
export const CONTEXT_DOCK_TIGHT_PX = 48;
export const CONTEXT_DOCK_WIDE_PX = 160;

export function contextCardWidth(railWidth: number, dockWide: boolean): number {
  return (
    Math.floor(railWidth) -
    CONTEXT_CARD_INSET_PX -
    (dockWide ? CONTEXT_DOCK_WIDE_PX : CONTEXT_DOCK_TIGHT_PX)
  );
}

/**
 * The inline filter, the delta line and the Media grid appear from this card
 * width — the width a 300px rail's card had beside the tight dock, so every
 * tight-dock story reads exactly as before (RL-CAN-10/15 at 300).
 */
export const CONTEXT_FILTER_INLINE_CARD_PX = 238;

/**
 * DATED 2026-09-07: the card's floor beside a WIDE dock. The lens head must
 * keep its name, its count and its two tools on one row; below this the
 * dock renders tight (the stored preference untouched) and its toggle
 * withdraws, instead of a card a few characters wide.
 */
export const CONTEXT_CARD_FLOOR_PX = 176;

/**
 * DATED 2026-10-08 (the principal: "instead of hiding it, if clicked, make
 * the rail the size it needs to be"): the narrowest rail a wide dock stands
 * in. The toggle never withdraws; pressing Expand below this grows the rail
 * to it, and Compact gives that growth back.
 */
export const CONTEXT_RAIL_WIDE_DOCK_MIN_PX =
  CONTEXT_CARD_FLOOR_PX + CONTEXT_CARD_INSET_PX + CONTEXT_DOCK_WIDE_PX;

/**
 * Media columns step at these card widths (the tight-dock card at a 300, 480
 * and 640 rail); comfortable density starts at two, compact at three (D-RL3:
 * the one user-theme vocabulary, no rail-local latch).
 */
export const MEDIA_GRID_CARD_STEPS: ReadonlyArray<number> = [238, 418, 578];
export type MediaGridColumns = 2 | 3 | 4 | 5;

export function mediaGridColumnsForCard(
  cardWidth: number,
  density: UserThemeDensity = "comfortable",
): MediaGridColumns {
  const steps = MEDIA_GRID_CARD_STEPS.filter((step) => cardWidth >= step).length;
  const columns = Math.max(2, 1 + steps) + (density === "compact" ? 1 : 0);
  return Math.min(5, columns) as MediaGridColumns;
}
